<template>
  <div class="col-sm-12 px-0">
    <div class="settings-box position-relative">
      <div
        v-if="pending"
        class="text-center py-3"
      >
        <Loader />
      </div>
      <div
        v-else
        style="max-width: 560px"
      >
        <div class="settings-title pb-2">
          {{ $t('settings.mobileApp.webTab') }}
        </div>
        <div class="text-muted small mb-3">
          {{ $t('settings.mobileApp.webTabInfo') }}
        </div>

        <div
          v-if="error"
          class="alert alert-danger small"
        >
          {{ error }}
        </div>

        <BFormGroup :label="$t('settings.mobileApp.url')">
          <BFormInput
            v-model="form.url"
            type="url"
            placeholder="https://"
            :state="urlValid ? null : false"
          />
          <div class="text-muted small mt-1">
            {{ $t('settings.mobileApp.urlHint') }}
          </div>
        </BFormGroup>

        <BFormGroup :label="$t('settings.mobileApp.title')">
          <BFormInput
            v-model="form.title"
            :maxlength="titleMaxLength"
            :state="!form.url.trim() || form.title.trim() ? null : false"
          />
          <div class="text-muted small text-right">
            {{ form.title.length }}/{{ titleMaxLength }}
          </div>
        </BFormGroup>

        <BFormGroup :label="$t('settings.mobileApp.icon')">
          <div class="d-flex align-items-center">
            <div
              v-if="form.icon"
              class="tab-icon-preview mr-3"
            >
              <!-- Tinted through a mask, like the app does with the tab icon -->
              <div
                class="tab-icon"
                :style="iconMaskStyle"
              />
            </div>
            <label class="btn btn-sm btn-outline-secondary mb-0">
              <i class="far fa-image mr-1" />
              {{ form.icon ? $t('general.change') : $t('settings.mobileApp.selectIcon') }}
              <input
                type="file"
                accept="image/png,image/svg+xml"
                class="d-none"
                @change="onIconSelected"
              >
            </label>
            <button
              v-if="form.icon"
              class="btn btn-sm btn-outline-danger ml-2"
              @click="form.icon = ''"
            >
              <i class="fas fa-times mr-1" />
              {{ $t('general.remove') }}
            </button>
          </div>
          <div
            v-if="iconError"
            class="text-danger small mt-1"
          >
            {{ iconError }}
          </div>
          <div class="text-muted small mt-1">
            {{ $t('settings.mobileApp.iconHint') }}
          </div>
        </BFormGroup>

        <div class="d-flex mt-3">
          <button
            class="btn btn-sm btn-primary"
            :disabled="!valid || saving"
            @click="save"
          >
            {{ $t('general.save') }}
          </button>
          <button
            v-if="saved.url"
            class="btn btn-sm btn-outline-danger ml-2"
            :disabled="saving"
            @click="clear"
          >
            {{ $t('settings.mobileApp.disable') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

// Rendered at ~24pt in the tab bar, so 96px covers 3x screens
const ICON_SIZE = 96;
const ICON_MAX_BYTES = 30 * 1024;

const emptyForm = () => ({ url: '', title: '', icon: '' });

const loadImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = e.target.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Rasterizes the icon to a square PNG, keeping transparency - the app tints
// the non-transparent pixels with the tab bar color.
const toPngIcon = (image) => {
  const width = image.width || ICON_SIZE;
  const height = image.height || ICON_SIZE;
  const scale = ICON_SIZE / Math.max(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = ICON_SIZE;
  canvas.height = ICON_SIZE;

  const ctx = canvas.getContext('2d');
  const w = width * scale;
  const h = height * scale;
  ctx.drawImage(image, (ICON_SIZE - w) / 2, (ICON_SIZE - h) / 2, w, h);

  const base64 = canvas.toDataURL('image/png').split(',')[1];
  return base64.length <= ICON_MAX_BYTES ? base64 : null;
};

export default {
  data: () => ({
    titleMaxLength: 20,
    pending: false,
    saving: false,
    error: null,
    iconError: null,
    saved: emptyForm(),
    form: emptyForm(),
  }),
  computed: {
    iconMaskStyle() {
      const mask = `url(data:image/png;base64,${this.form.icon}) center / contain no-repeat`;
      return { mask, '-webkit-mask': mask };
    },
    urlValid() {
      const url = this.form.url.trim();
      return !url || /^https:\/\/\S+$/i.test(url);
    },
    valid() {
      return !!this.form.url.trim() && this.urlValid && !!this.form.title.trim();
    },
  },
  methods: {
    ...mapActions(['getPayeeWebTab', 'setPayeeWebTab', 'clearPayeeWebTab']),
    async onIconSelected(e) {
      const [file] = e.target.files;
      e.target.value = '';
      if (!file) return;

      this.iconError = null;
      try {
        const icon = toPngIcon(await loadImage(file));
        if (icon) {
          this.form.icon = icon;
        } else {
          this.iconError = this.$t('settings.mobileApp.iconTooLarge');
        }
      } catch {
        this.iconError = this.$t('settings.mobileApp.iconInvalid');
      }
    },
    save() {
      const saved = {
        url: this.form.url.trim(),
        title: this.form.title.trim(),
        icon: this.form.icon,
      };
      // The icon is stored as a blob, so send it only when it changed
      const iconChanged = saved.icon !== this.saved.icon;

      this.persist(this.setPayeeWebTab({
        data: {
          url: saved.url,
          title: saved.title,
          icon: iconChanged && saved.icon ? saved.icon : undefined,
          removeIcon: iconChanged && !saved.icon,
        },
      }), saved);
    },
    clear() {
      this.persist(this.clearPayeeWebTab(), emptyForm());
    },
    persist(request, saved) {
      this.saving = true;
      this.error = null;

      request
        .then(() => {
          this.saved = saved;
          this.form = { ...emptyForm(), ...saved };
        })
        .catch(() => {
          this.error = this.$t('settings.mobileApp.saveError');
        })
        .finally(() => {
          this.saving = false;
        });
    },
    request() {
      this.pending = true;
      this.getPayeeWebTab()
        .then(({ data }) => {
          this.saved = {
            url: data.url || '',
            title: data.title || '',
            icon: data.icon || '',
          };
          this.form = { ...this.saved };
        })
        .catch(() => {
          this.error = this.$t('settings.mobileApp.loadError');
        })
        .finally(() => {
          this.pending = false;
        });
    },
  },
  created() {
    this.$emit('page', 'mobile-app');
    this.request();
  },
};
</script>

<style lang="scss" scoped>
.tab-icon-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: 1px solid #ddd;
  border-radius: 8px;

  .tab-icon {
    width: 28px;
    height: 28px;
    // The app's default tab color
    background-color: $blue;
  }
}
</style>
